import { formatCalendarDate } from '@/utils/dateFormat';

function roundCurrency(value: number): number {
    return Math.round(value * 100) / 100;
}

function roundPercentPoints(fraction: number): number {
    return Math.round(fraction * 1000) / 10;
}

function isZeroDisplay(value: number): boolean {
    return Object.is(value, -0) || value === 0;
}

function formatSigned(
    value: number,
    formatter: (abs: number) => string,
): string {
    const abs = Math.abs(value);
    const formatted = formatter(abs);
    return value < 0 ? `(${formatted})` : formatted;
}

export function formatCurrency(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const rounded = roundCurrency(value);
    if (isZeroDisplay(rounded)) {
        return '-';
    }
    return formatSigned(rounded, (abs) => {
        const formatted = abs.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `$${formatted}`;
    });
}

export function formatCurrencyWhole(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const rounded = Math.round(value);
    if (isZeroDisplay(rounded)) {
        return '-';
    }
    return formatSigned(rounded, (abs) => {
        const formatted = abs.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        return `$${formatted}`;
    });
}

export function formatPercent(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const points = roundPercentPoints(value);
    if (isZeroDisplay(points)) {
        return '-';
    }
    return formatSigned(points, (abs) => {
        const formatted = abs.toLocaleString('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        });
        return `${formatted}%`;
    });
}

export function formatNumber(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const rounded = roundCurrency(value);
    if (isZeroDisplay(rounded)) {
        return '-';
    }
    return formatSigned(rounded, (abs) =>
        abs.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }),
    );
}

export function formatDate(value: string | null | undefined): string {
    return formatCalendarDate(value, '-');
}

function isoDateOnly(value: string): string {
    return value.slice(0, 10);
}

function addCalendarDays(isoDate: string, days: number): string {
    const [year, month, day] = isoDateOnly(isoDate).split('-');
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
    date.setUTCDate(date.getUTCDate() + days);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function expirationDueSoon(
    expiresOn: string,
    today: string,
    windowDays = 30,
): boolean {
    const expires = isoDateOnly(expiresOn);
    const threshold = addCalendarDays(isoDateOnly(today), windowDays);
    return expires <= threshold;
}

/** Compact money in $000s (e.g. $70.7k, $2400.5k). */
export function formatSummaryMoney(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const rounded = roundCurrency(value);
    if (isZeroDisplay(rounded)) {
        return '-';
    }
    return formatSigned(rounded, (abs) => {
        const kVal = Math.round((abs / 1000) * 10) / 10;
        return `$${kVal}k`;
    });
}

/** Avg annual salary rows: $#,##0.0k with parentheses for negatives. */
export function formatSalaryAnnualK(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return '-';
    }
    const rounded = roundCurrency(value);
    if (isZeroDisplay(rounded)) {
        return '-';
    }
    return formatSigned(rounded, (abs) => {
        const kVal = Math.round((abs / 1000) * 10) / 10;
        const formatted = kVal.toLocaleString('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        });
        return `$${formatted}k`;
    });
}

const MONTH_ABBREV = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Format YYYY-MM as Mon-YY (e.g. Sep-26). */
export function formatMonthLabel(yyyyMm: string): string {
    const match = /^(\d{4})-(\d{2})$/.exec(yyyyMm.trim());
    if (!match) {
        return yyyyMm;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return yyyyMm;
    }
    const yy = String(year % 100).padStart(2, '0');
    return `${MONTH_ABBREV[month - 1]}-${yy}`;
}

/** Format ISO date as Mon D (e.g. Sep 4) for pay-date lists. */
export function formatPayDateLabel(isoDate: string): string {
    const part = isoDate.trim().slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
    if (!match) {
        return isoDate;
    }
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12) {
        return isoDate;
    }
    return `${MONTH_ABBREV[month - 1]} ${day}`;
}

export function formatByUnit(
    unit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct' | 'text',
    value: number | string | null | undefined,
): string {
    if (unit === 'text') {
        if (value == null || value === '') {
            return '-';
        }
        return String(value);
    }
    if (unit === 'usd_full') {
        return formatCurrency(typeof value === 'number' ? value : null);
    }
    if (unit === 'usd_salary_k') {
        return formatSalaryAnnualK(typeof value === 'number' ? value : null);
    }
    if (unit === 'usd') {
        return formatSummaryMoney(typeof value === 'number' ? value : null);
    }
    if (unit === 'pct') {
        return formatPercent(typeof value === 'number' ? value : null);
    }
    return formatNumber(typeof value === 'number' ? value : null);
}
